import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Download } from "lucide-react";
import { JSX, useState } from "react";
import { mediaItems } from "@/data";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/media")({
  component: MediaPage,
});

const categories = ["All", "Events", "Training", "Posters"];

// eslint-disable-next-line react-refresh/only-export-components
function MediaPage(): JSX.Element {
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = mediaItems.filter(
    (m) => activeCategory === "All" || m.category === activeCategory,
  );

  return (
    <>
      <Helmet>
        <title>Media Centre | Rosewood Tax Consultancy Kenya</title>
        <meta
          name="description"
          content="Browse Rosewood Tax Consultancy's media library — event photos, training videos, and informational posters."
        />
      </Helmet>

      <section className="bg-[#FAF7F2] pt-16 pb-14">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-serif text-5xl md:text-6xl font-semibold text-[#1A1A1A] mb-4">
              Events & <span className=" text-[#6B1E2A]">Media</span>
            </h1>
            <p className="text-lg text-[#6B6B6B] max-w-xl">
              Stay updated with our latest events, training sessions, and
              informational resources.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={cn(
                  "px-5 py-2 rounded-lg text-sm font-medium transition-all",
                  activeCategory === cat
                    ? "bg-[#6B1E2A] text-white"
                    : "bg-[#FAF7F2] text-[#6B6B6B]",
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#6B6B6B]">No media found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="group bg-[#FAF7F2] rounded-xl overflow-hidden hover:-translate-y-0.5 transition-all cursor-pointer"
                >
                  <div className="aspect-video bg-[#6B1E2A]/8 relative flex items-center justify-center">


                  </div>

                  {/* Info */}
                  <div className="p-5">
                    <span className="text-xs font-medium text-[#C9A84C] uppercase tracking-wide">
                      {item.category}
                    </span>
                    <h3 className="font-semibold text-[#1A1A1A] mt-1 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#6B6B6B] line-clamp-2 mb-4">
                      {item.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#6B6B6B]">
                        {new Date(item.uploadedAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      {item.type === "poster" && (
                        <button className="flex items-center gap-1.5 text-xs font-semibold text-[#6B1E2A] hover:text-[#4A1219] transition-colors">
                          <Download size={13} />
                          Download
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

        </div>
      </section>
    </>
  );
}
